import React, { useState } from 'react';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import { ITaskSummaryEmail, TASK_SUMMARY_EMAIL_QUERY_KEY, updateTaskSummaryEmail, useTaskSummaryEmailQuery } from '../../api/admin';
import { apiErrorMessage } from '../../utils/api';

/** ISO weekdays, as stored: 1 = Monday .. 7 = Sunday. */
const DAYS = [
	{ value: 1, label: 'Mon' },
	{ value: 2, label: 'Tue' },
	{ value: 3, label: 'Wed' },
	{ value: 4, label: 'Thu' },
	{ value: 5, label: 'Fri' },
	{ value: 6, label: 'Sat' },
	{ value: 7, label: 'Sun' },
];
const WEEKDAYS = [1, 2, 3, 4, 5];
const DAILY = [1, 2, 3, 4, 5, 6, 7];

const sameDays = (a: number[], b: number[]) => a.length === b.length && a.every((d) => b.includes(d));

/** "Off", "Daily", "Weekdays", or the picked days: what the button shows. */
function summaryLabel(setting: ITaskSummaryEmail): string {
	if (!setting.is_active) return 'Off';
	if (sameDays(setting.days, DAILY)) return 'Daily';
	if (sameDays(setting.days, WEEKDAYS)) return 'Weekdays';
	return DAYS.filter((d) => setting.days.includes(d.value))
		.map((d) => d.label)
		.join(', ');
}

/**
 * The signed-in admin's own switch for the open-tasks summary email: off, daily, weekdays, or
 * a pick of days. Every click saves straight away; the send time is the scheduler's, not ours.
 */
export default function TaskSummaryEmailMenu() {
	const queryClient = useQueryClient();
	const { data: setting, isLoading } = useTaskSummaryEmailQuery();
	const [anchor, setAnchor] = useState<HTMLElement | null>(null);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	async function save(next: ITaskSummaryEmail, closeAfter: boolean) {
		setSaving(true);
		setError(null);
		try {
			const saved = await updateTaskSummaryEmail(next);
			queryClient.setQueryData(TASK_SUMMARY_EMAIL_QUERY_KEY, saved);
			if (closeAfter) setAnchor(null);
		} catch (e: any) {
			setError(apiErrorMessage(e, 'Could not save. Please try again.'));
		} finally {
			setSaving(false);
		}
	}

	function toggleDay(day: number) {
		if (!setting) return;
		const current = setting.is_active ? setting.days : [];
		const days = current.includes(day) ? current.filter((d) => d !== day) : [...current, day];
		// Unticking the last day is "off"; the stored days stay as they were.
		if (days.length === 0) return save({ is_active: false, days: setting.days }, false);
		return save({ is_active: true, days }, false);
	}

	const checked = (day: number) => !!setting?.is_active && setting.days.includes(day);

	return (
		<>
			<Button
				variant="outlined"
				startIcon={<MailOutlineIcon />}
				onClick={(e) => setAnchor(e.currentTarget)}
				disabled={isLoading || !setting}
			>
				Summary email: {setting ? summaryLabel(setting) : '…'}
			</Button>
			<Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
				{error && (
					<Typography variant="caption" color="error" sx={{ display: 'block', px: 2, pb: 1 }}>
						{error}
					</Typography>
				)}
				<MenuItem
					dense
					selected={!!setting && !setting.is_active}
					disabled={saving}
					onClick={() => setting && save({ is_active: false, days: setting.days }, true)}
				>
					Off
				</MenuItem>
				<MenuItem
					dense
					selected={!!setting?.is_active && sameDays(setting.days, DAILY)}
					disabled={saving}
					onClick={() => save({ is_active: true, days: DAILY }, true)}
				>
					Daily
				</MenuItem>
				<MenuItem
					dense
					selected={!!setting?.is_active && sameDays(setting.days, WEEKDAYS)}
					disabled={saving}
					onClick={() => save({ is_active: true, days: WEEKDAYS }, true)}
				>
					Weekdays
				</MenuItem>
				<Divider />
				{DAYS.map((d) => (
					<MenuItem key={d.value} dense disabled={saving} onClick={() => toggleDay(d.value)} sx={{ py: 0 }}>
						<Checkbox size="small" checked={checked(d.value)} tabIndex={-1} disableRipple sx={{ p: 0.5, mr: 1 }} />
						<ListItemText primary={d.label} />
					</MenuItem>
				))}
			</Menu>
		</>
	);
}
